import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';

const ROLE_LABELS = {
  manager:       { fr: 'Manager Arcus',      en: 'Arcus Manager',       color: '#7C3AED', bg: '#EDE9FE' },
  souscripteur:  { fr: 'Souscripteur Arcus', en: 'Arcus Underwriter',   color: '#F18A00', bg: '#FFF7F2' },
  courtier:      { fr: 'Courtier',           en: 'Broker',              color: '#0369A1', bg: '#EFF6FF' },
  client:        { fr: 'Client',             en: 'Client',              color: '#065F46', bg: '#ECFDF5' },
};

export default function LoginPage() {
  const { login }  = useAuth();
  const { t }      = useLanguage();
  const navigate   = useNavigate();

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd]   = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) { setError(t('auth.fill_all')); return; }
    setLoading(true); setError('');
    try {
      await login(email.trim(), password);
      navigate('/', { replace: true });
    } catch(err) {
      setError(err.message);
    } finally { setLoading(false); }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1A1A1A 0%, #2D2D2D 50%, #1A1A1A 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20,
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>

      {/* Decorative orange accent top-left */}
      <div style={{
        position: 'fixed', top: 0, left: 0,
        width: 200, height: 4,
        background: 'linear-gradient(90deg, #F18A00, transparent)',
      }}/>

      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ fontSize: 30, fontWeight: 900, letterSpacing: '-1px', color: '#fff' }}>
            ARCUS<span style={{ color: '#F18A00' }}>CONNECT</span>
          </div>
          <div style={{ fontSize: 12, color: '#64748B', marginTop: 4, letterSpacing: '.12em', textTransform: 'uppercase' }}>
            CRM
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: '#fff',
          borderRadius: 16,
          padding: 36,
          boxShadow: '0 25px 60px rgba(0,0,0,.4)',
        }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 6px', color: '#1A1A1A' }}>
            {t('auth.welcome')}
          </h1>
          <p style={{ fontSize: 13, color: '#64748B', margin: '0 0 28px' }}>
            {t('auth.sign_in_desc')}
          </p>

          {/* Error */}
          {error && (
            <div style={{
              background: '#FEE2E2', color: '#991B1B', borderRadius: 8,
              padding: '10px 14px', fontSize: 13, marginBottom: 18,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              ⚠ {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Email */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('auth.email_placeholder')}
                autoComplete="email"
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box',
                  border: '1.5px solid #E5E7EB', borderRadius: 8,
                  padding: '11px 14px', fontSize: 14, fontFamily: 'inherit',
                  outline: 'none', transition: 'border-color .15s',
                }}
                onFocus={e => e.target.style.borderColor = '#F18A00'}
                onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 6 }}>
                {t('auth.password')}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPwd ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('auth.password_placeholder')}
                  autoComplete="current-password"
                  style={{
                    width: '100%', boxSizing: 'border-box',
                    border: '1.5px solid #E5E7EB', borderRadius: 8,
                    padding: '11px 44px 11px 14px', fontSize: 14, fontFamily: 'inherit',
                    outline: 'none', transition: 'border-color .15s',
                  }}
                  onFocus={e => e.target.style.borderColor = '#F18A00'}
                  onBlur={e  => e.target.style.borderColor = '#E5E7EB'}
                />
                <button type="button" onClick={() => setShowPwd(p => !p)}
                  style={{
                    position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 16, color: '#9CA3AF', padding: 2,
                  }}>
                  {showPwd ? '🙈' : '👁'}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              style={{
                width: '100%', padding: '12px',
                background: loading ? '#FF9C26' : '#F18A00',
                color: '#fff', border: 'none', borderRadius: 8,
                fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'opacity .15s', opacity: loading ? .7 : 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
              {loading ? (
                <>
                  <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid rgba(255,255,255,.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }}/>
                  {t('auth.signing_in')}
                </>
              ) : t('auth.sign_in')}
            </button>

            {/* Mot de passe oublié */}
            <div style={{ textAlign: 'center', marginTop: 14 }}>
              <a href="/forgot-password" style={{ fontSize: 13, color: '#64748B', textDecoration: 'none', borderBottom: '1px solid transparent', transition: 'color .12s, border-color .12s' }}
                onMouseEnter={e => { e.target.style.color = '#F18A00'; e.target.style.borderBottomColor = '#F18A00'; }}
                onMouseLeave={e => { e.target.style.color = '#64748B'; e.target.style.borderBottomColor = 'transparent'; }}>
                {t('auth.forgot_password')}
              </a>
            </div>
          </form>

          {/* Role legend */}
          <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>
              {t('auth.access_levels')}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {Object.entries(ROLE_LABELS).map(([role, info]) => (
                <div key={role} style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  background: info.bg, borderRadius: 6, padding: '5px 9px',
                }}>
                  <div style={{ width: 7, height: 7, borderRadius: '50%', background: info.color, flexShrink: 0 }}/>
                  <span style={{ fontSize: 11.5, fontWeight: 600, color: info.color }}>
                    {info.fr}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11, color: '#4B5563' }}>
          Arcus Solutions · arcus-solutions.fr
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
